import {
  DocumentData,
  RigaSelezionabile,
  SelezionePrenotazione,
} from "../../types";

// Selezione delle righe da prenotare nelle tab di ricerca. La regola è la
// stessa che i checkbox applicavano scrivendo `disabled` nel DOM: una volta
// scelta una riga, si possono aggiungere solo righe dello stesso corso, con lo
// stesso corso da prenotare, e dello stesso tipo (mancanti con mancanti,
// attestati esistenti con esistenti), perché la prenotazione parte per un
// corso solo. Il corso da prenotare lo sceglie il backend per discente
// (ADR-0025): due attestati dello stesso corso possono rinnovarsi uno con
// l'aggiornamento e l'altro, finito da più di dieci anni, con il corso
// completo. Qui la regola è una funzione pura e lo stato `disabled` di ogni
// checkbox si deriva dalla selezione.

// Un corso fuori catalogo arriva senza codice: il confronto non deve cadere.
function stessoCorso(a: string, b: string): boolean {
  return (a ?? "").toLowerCase() === (b ?? "").toLowerCase();
}

export function selezionabile(
  selezione: SelezionePrenotazione,
  riga: RigaSelezionabile
): boolean {
  if (selezione.length === 0) return true;
  const prima = selezione[0];
  return (
    stessoCorso(prima.codiceCorso, riga.codiceCorso) &&
    stessoCorso(prima.codicePrenotazione, riga.codicePrenotazione) &&
    prima.mancante === riga.mancante
  );
}

// La chiave di una riga della lista attestati, la stessa per la selezione e
// per l'id del DOM della sua casella. Il discente e il corso non bastano: la
// stessa persona può avere due attestati dello stesso corso (id DocuManager
// diversi), e sono due righe con due caselle. L'id da solo nemmeno: i corsi
// mancanti e gli attestati DocuManager hanno id nello stesso spazio numerico e
// possono coincidere, quindi la chiave porta anche il tipo.
export function chiaveRiga(a: {
  id: DocumentData["id"];
  metadata: Pick<DocumentData["metadata"], "mancante">;
}): string {
  return `${a.metadata.mancante ? "mancante" : "attestato"}_${a.id}`;
}

// La casella di una riga è spuntata se e solo se la riga è nella selezione:
// le caselle sono controllate, così restano coerenti anche quando la riga
// sparisce dalla ricerca e ricompare.
export function selezionata(
  selezione: SelezionePrenotazione,
  chiave: string
): boolean {
  return selezione.some((r) => r.chiave === chiave);
}

// Aggiunge la riga se assente, la toglie se presente. Non muta la selezione
// in ingresso.
export function toggleSelezione(
  selezione: SelezionePrenotazione,
  riga: RigaSelezionabile
): SelezionePrenotazione {
  return selezionata(selezione, riga.chiave)
    ? senzaRiga(selezione, riga.chiave)
    : [...selezione, riga];
}

// Toglie la riga con quella chiave, se c'è: serve quando la riga cambia
// discente (un mancante a cui si annulla l'assegnazione), perché la selezione
// porterebbe ancora il discente di prima. Senza la riga restituisce la
// selezione così com'è.
export function senzaRiga(
  selezione: SelezionePrenotazione,
  chiave: string
): SelezionePrenotazione {
  return selezionata(selezione, chiave)
    ? selezione.filter((r) => r.chiave !== chiave)
    : selezione;
}

// I partecipanti da portare alla prenotazione: due righe spuntate della stessa
// persona (due attestati dello stesso corso) la prenotano una volta sola. Il
// codice fiscale si confronta senza badare alle maiuscole e resta nella forma
// della prima riga che lo porta, nell'ordine della selezione.
export function partecipanti(selezione: SelezionePrenotazione): string[] {
  const visti = new Set<string>();
  const cf: string[] = [];
  selezione.forEach((r) => {
    const chiave = r.cf.toUpperCase();
    if (visti.has(chiave)) return;
    visti.add(chiave);
    cf.push(r.cf);
  });
  return cf;
}
